import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Plus, Search, Building2, X, Loader2, Trash2, AlertTriangle, RotateCcw, Tag, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import TagInput from '../components/TagInput';
import DataTable from '../components/table/DataTable';
import ViewSelector from '../components/table/ViewSelector';
import AddColumnPanel from '../components/table/AddColumnPanel';
import { CONTACT_FIELD_REGISTRY, DEFAULT_CONTACT_COLUMNS, buildVisibleColumns, getAllFields } from '../components/table/columnRegistry';
import useTableView from '../hooks/useTableView';
import useCustomFields from '../hooks/useCustomFields';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = ['Champion', 'Decision Maker', 'Influencer', 'Blocker', 'User'];
const SENIORITY = ['C-suite', 'VP', 'Director', 'Manager', 'IC'];

function ContactModal({ open, onClose, contact, accounts, onSave, onAccountCreated }) {
    const { getAuthHeader } = useAuth();
    const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', mobile: '', title: '', department: '', seniority: '', linkedin_url: '', role_in_deal: '', notes: '', account_id: '' });
    const [loading, setLoading] = useState(false);
    const [accountSearch, setAccountSearch] = useState('');
    const [showAccountDropdown, setShowAccountDropdown] = useState(false);
    const [showNewCompany, setShowNewCompany] = useState(false);
    const [newCompanyName, setNewCompanyName] = useState('');
    const [creatingCompany, setCreatingCompany] = useState(false);
    const accountDropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (accountDropdownRef.current && !accountDropdownRef.current.contains(e.target)) {
                setShowAccountDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (contact) {
            setForm(contact);
            const acc = accounts.find(a => a.account_id === contact.account_id);
            setAccountSearch(acc?.name || '');
        } else {
            setForm({ first_name: '', last_name: '', email: '', phone: '', mobile: '', title: '', department: '', seniority: '', linkedin_url: '', role_in_deal: '', notes: '', account_id: '' });
            setAccountSearch('');
        }
        setShowNewCompany(false);
        setNewCompanyName('');
    }, [contact, open]);

    const filteredAccounts = accounts.filter(a =>
        a.name.toLowerCase().includes(accountSearch.toLowerCase())
    );

    const selectAccount = (acc) => {
        setForm({ ...form, account_id: acc.account_id });
        setAccountSearch(acc.name);
        setShowAccountDropdown(false);
    };

    const clearAccount = () => {
        setForm({ ...form, account_id: '' });
        setAccountSearch('');
    };

    const handleCreateCompany = async () => {
        if (!newCompanyName.trim()) return toast.error('Company name required');
        setCreatingCompany(true);
        try {
            const res = await axios.post(`${API}/accounts`, { name: newCompanyName.trim(), status: 'prospect' }, { headers: getAuthHeader() });
            const newAcc = res.data;
            toast.success(`Company "${newAcc.name}" created`);
            setForm({ ...form, account_id: newAcc.account_id });
            setAccountSearch(newAcc.name);
            setShowNewCompany(false);
            setNewCompanyName('');
            if (onAccountCreated) onAccountCreated(newAcc);
        } catch { toast.error('Failed to create company'); }
        finally { setCreatingCompany(false); }
    };

    const handleSave = async () => {
        if (!form.first_name || !form.last_name) return toast.error('First and last name required');
        setLoading(true);
        try {
            let res;
            if (contact) res = await axios.put(`${API}/contacts/${contact.contact_id}`, form, { headers: getAuthHeader() });
            else res = await axios.post(`${API}/contacts`, form, { headers: getAuthHeader() });
            toast.success(contact ? 'Contact updated' : 'Contact created');
            onSave(res.data);
            onClose();
        } catch { toast.error('Failed to save contact'); }
        finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{contact ? 'Edit Contact' : 'New Contact'}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 mt-2">
                    {[
                        { label: 'First Name *', key: 'first_name' }, { label: 'Last Name *', key: 'last_name' },
                        { label: 'Personal Email', key: 'email', type: 'email' }, { label: 'Personal Phone', key: 'phone' },
                        { label: 'Mobile/WhatsApp', key: 'mobile' },
                        { label: 'Job Title', key: 'title' }, { label: 'Department', key: 'department' },
                        { label: 'Personal LinkedIn URL', key: 'linkedin_url' },
                    ].map(f => (
                        <div key={f.key}>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">{f.label}</label>
                            <input type={f.type || 'text'} value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                                data-testid={`contact-${f.key}-input`}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-400" />
                        </div>
                    ))}
                    <div className="relative" ref={accountDropdownRef}>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Company</label>
                        {!showNewCompany ? (
                            <div className="relative">
                                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
                                <input
                                    type="text"
                                    value={accountSearch}
                                    onChange={e => { setAccountSearch(e.target.value); setShowAccountDropdown(true); if (!e.target.value) setForm({ ...form, account_id: '' }); }}
                                    onFocus={() => setShowAccountDropdown(true)}
                                    placeholder="Search companies..."
                                    data-testid="contact-account-search"
                                    className="w-full pl-9 pr-8 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-400"
                                />
                                {form.account_id && (
                                    <button onClick={clearAccount} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-stone-400 hover:text-stone-600" type="button">
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                )}
                                {showAccountDropdown && (
                                    <div className="absolute z-50 mt-1 w-full bg-white border border-stone-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="contact-account-dropdown">
                                        {filteredAccounts.map(a => (
                                            <button key={a.account_id} type="button" onClick={() => selectAccount(a)}
                                                className={`w-full text-left px-3 py-2 text-sm hover:bg-stone-50 flex items-center justify-between ${form.account_id === a.account_id ? 'bg-stone-50 font-medium' : ''}`}
                                                data-testid={`account-option-${a.account_id}`}>
                                                <span className="truncate">{a.name}</span>
                                                {a.industry && <span className="text-xs text-stone-400 ml-2 flex-shrink-0">{a.industry}</span>}
                                            </button>
                                        ))}
                                        {filteredAccounts.length === 0 && accountSearch && (
                                            <div className="px-3 py-2 text-sm text-stone-400">No companies match "{accountSearch}"</div>
                                        )}
                                        <button type="button" onClick={() => { setShowNewCompany(true); setNewCompanyName(accountSearch); setShowAccountDropdown(false); }}
                                            className="w-full text-left px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 border-t border-stone-100 flex items-center gap-2 font-medium"
                                            data-testid="add-new-company-btn">
                                            <Plus className="w-3.5 h-3.5" /> Add New Company{accountSearch ? `: "${accountSearch}"` : ''}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex gap-2" data-testid="new-company-inline">
                                <input type="text" value={newCompanyName} onChange={e => setNewCompanyName(e.target.value)}
                                    placeholder="New company name" autoFocus
                                    data-testid="new-company-name-input"
                                    className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                <button type="button" onClick={handleCreateCompany} disabled={creatingCompany}
                                    className="px-3 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50"
                                    data-testid="create-company-btn">
                                    {creatingCompany ? '...' : 'Create'}
                                </button>
                                <button type="button" onClick={() => setShowNewCompany(false)}
                                    className="px-2 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-500">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Seniority</label>
                        <select value={form.seniority || ''} onChange={e => setForm({ ...form, seniority: e.target.value })} data-testid="contact-seniority-select"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                            <option value="">Select...</option>
                            {SENIORITY.map(s => <option key={s}>{s}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Role in Deal</label>
                        <select value={form.role_in_deal || ''} onChange={e => setForm({ ...form, role_in_deal: e.target.value })} data-testid="contact-role-select"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                            <option value="">Select...</option>
                            {ROLES.map(r => <option key={r}>{r}</option>)}
                        </select>
                    </div>
                    <div className="col-span-2">
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Notes</label>
                        <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-4">
                    <button onClick={onClose} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                    <button onClick={handleSave} disabled={loading} data-testid="contact-save-btn"
                        className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50">
                        {loading ? 'Saving...' : 'Save Contact'}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function Contacts() {
    const { getAuthHeader, user } = useAuth();
    const { can } = usePermissions();
    const navigate = useNavigate();

    const [contacts, setContacts] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterOwner, setFilterOwner] = useState(user?.user_id || ''); // Default to current user
    const [filterSeniority, setFilterSeniority] = useState('');
    // v2.16.39 BUG-039: tag filter on the Contacts toolbar. The grid
    // already paginated/filtered at the backend level via ?tag=.
    const [filterTag, setFilterTag] = useState('');
    const [tagOptions, setTagOptions] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editContact, setEditContact] = useState(null);
    const [total, setTotal] = useState(0);
    const [teamMembers, setTeamMembers] = useState([]);

    // Selection & bulk delete
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);

    // Deleted tab
    const [activeTab, setActiveTab] = useState('contacts'); // 'contacts' | 'deleted'
    const [deletedContacts, setDeletedContacts] = useState([]);
    const [deletedTotal, setDeletedTotal] = useState(0);
    const [restoring, setRestoring] = useState(false);
    const [showBulkTagModal, setShowBulkTagModal] = useState(false);
    const [bulkTagAction, setBulkTagAction] = useState('add');
    const [bulkTags, setBulkTags] = useState([]);
    const [bulkTagging, setBulkTagging] = useState(false);
    // BUG-040 — bulk-enroll into a campaign
    const [showBulkEnrollModal, setShowBulkEnrollModal] = useState(false);
    const [campaignsList, setCampaignsList] = useState([]);
    const [selectedCampaignId, setSelectedCampaignId] = useState('');
    const [enrolling, setEnrolling] = useState(false);

    // Table view management
    const {
        views, activeViewId, visibleColumnKeys, columnSizing, sorting, isDirty, loaded,
        switchView, saveView, saveAsView, deleteView, pushToOrg, addColumn, removeColumn,
        setSorting, setColumnSizing,
    } = useTableView('contact', DEFAULT_CONTACT_COLUMNS);

    // Custom fields
    const { fields: customFields } = useCustomFields('contact');

    // Build visible column configs
    const visibleColumns = buildVisibleColumns(CONTACT_FIELD_REGISTRY,
        visibleColumnKeys.map((key, idx) => ({ field_key: key, visible: true, order: idx, width: columnSizing[key] })),
        customFields
    );
    const allFields = getAllFields(CONTACT_FIELD_REGISTRY, customFields);
    const visibleKeySet = new Set(visibleColumnKeys);

    // Load team members for owner filter
    useEffect(() => {
        const loadTeam = async () => {
            try {
                const res = await axios.get(`${API}/accounts/team-members`, { headers: getAuthHeader() });
                setTeamMembers(res.data || []);
            } catch { /* ignore */ }
        };
        loadTeam();
    }, [getAuthHeader]);

    // v2.16.39 BUG-039: populate the tag-filter dropdown from the tracker's
    // contact-scoped tag aggregation.
    useEffect(() => {
        const loadTags = async () => {
            try {
                const res = await axios.get(`${API}/tags?entity=contact`, { headers: getAuthHeader() });
                setTagOptions(Array.isArray(res.data) ? res.data : []);
            } catch { /* ignore */ }
        };
        loadTags();
    }, [getAuthHeader]);

    // Load accounts for the contact modal
    useEffect(() => {
        const loadAccounts = async () => {
            try {
                const res = await axios.get(`${API}/accounts?page_size=500`, { headers: getAuthHeader() });
                const data = Array.isArray(res.data) ? res.data : (res.data.data || []);
                setAccounts(data);
            } catch { /* ignore */ }
        };
        loadAccounts();
    }, [getAuthHeader]);

    // Load contacts with filters
    const loadContacts = useCallback(async (retryCount = 0) => {
        try {
            const params = new URLSearchParams();
            if (search) params.set('search', search);
            if (filterOwner) params.set('owner_id', filterOwner);
            if (filterSeniority) params.set('seniority', filterSeniority);
            if (filterTag) params.set('tag', filterTag);
            params.set('page_size', '200');

            const res = await axios.get(`${API}/contacts?${params}`, { headers: getAuthHeader() });
            if (Array.isArray(res.data)) {
                setContacts(res.data);
                setTotal(res.data.length);
            } else {
                setContacts(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            if (retryCount < 1 && (!err?.response || err?.message === 'Network Error')) {
                setTimeout(() => loadContacts(retryCount + 1), 1000);
                return;
            }
        }
        finally { setLoading(false); }
    }, [search, filterOwner, filterSeniority, filterTag, getAuthHeader]);

    useEffect(() => { loadContacts(); }, [loadContacts]);

    const handleSave = (saved) => {
        setContacts(prev => {
            const idx = prev.findIndex(c => c.contact_id === saved.contact_id);
            if (idx >= 0) { const u = [...prev]; u[idx] = saved; return u; }
            return [saved, ...prev];
        });
        // Reload to get enriched data (account_name, owner_name)
        loadContacts();
    };

    // Load deleted contacts
    const loadDeletedContacts = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/contacts/deleted`, { headers: getAuthHeader() });
            setDeletedContacts(res.data.data || []);
            setDeletedTotal(res.data.total || 0);
        } catch { /* ignore - user may not have permission */ }
    }, [getAuthHeader]);

    useEffect(() => {
        if (activeTab === 'deleted') loadDeletedContacts();
    }, [activeTab, loadDeletedContacts]);

    const handleBulkDelete = async () => {
        if (deleteConfirmText !== 'DELETE') return;
        setDeleting(true);
        try {
            const ids = Array.from(selectedIds);
            await axios.delete(`${API}/contacts/bulk`, { data: { contact_ids: ids }, headers: getAuthHeader() });
            toast.success(`${ids.length} contact${ids.length > 1 ? 's' : ''} deleted`);
            setSelectedIds(new Set());
            setShowDeleteModal(false);
            setDeleteConfirmText('');
            loadContacts();
        } catch { toast.error('Failed to delete contacts'); }
        finally { setDeleting(false); }
    };

    const handleRestore = async (ids) => {
        const restoreIds = ids || Array.from(selectedIds);
        if (!restoreIds.length) return;
        setRestoring(true);
        try {
            await axios.put(`${API}/contacts/restore`, { contact_ids: restoreIds }, { headers: getAuthHeader() });
            toast.success(`${restoreIds.length} contact${restoreIds.length > 1 ? 's' : ''} restored`);
            setSelectedIds(new Set());
            loadDeletedContacts();
            loadContacts();
        } catch { toast.error('Failed to restore contacts'); }
        finally { setRestoring(false); }
    };

    // BUG-040 — load active + draft campaigns for the bulk-enroll picker
    const loadCampaignsForEnroll = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/campaigns`, { headers: getAuthHeader() });
            const items = Array.isArray(res.data) ? res.data : (res.data.data || res.data.campaigns || []);
            setCampaignsList(items);
        } catch (err) {
            console.error('Failed to load campaigns:', err);
            toast.error('Failed to load campaigns');
        }
    }, [getAuthHeader]);

    const handleBulkEnroll = async () => {
        if (!selectedCampaignId) { toast.error('Please select a campaign'); return; }
        setEnrolling(true);
        try {
            const res = await axios.post(`${API}/campaigns/${selectedCampaignId}/bulk-enroll`, {
                contact_ids: Array.from(selectedIds),
            }, { headers: getAuthHeader() });
            const { enrolled = 0, skipped = 0 } = res.data || {};
            toast.success(`${enrolled} contact${enrolled !== 1 ? 's' : ''} enrolled${skipped ? `, ${skipped} skipped (already enrolled or invalid)` : ''}`);
            setShowBulkEnrollModal(false);
            setSelectedCampaignId('');
            setSelectedIds(new Set());
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || 'Network error';
            toast.error(`Failed to enroll: ${detail}`, { duration: 8000 });
            console.error('Bulk-enroll failed:', err);
        }
        finally { setEnrolling(false); }
    };

    const handleBulkTag = async () => {
        if (!bulkTags.length) { toast.error('Please add at least one tag'); return; }
        setBulkTagging(true);
        try {
            const res = await axios.post(`${API}/contacts/bulk-tag`, {
                contact_ids: Array.from(selectedIds),
                action: bulkTagAction,
                tags: bulkTags,
            }, { headers: getAuthHeader() });
            toast.success(`Tags ${bulkTagAction === 'add' ? 'added to' : 'removed from'} ${res.data.count} contacts`);
            setShowBulkTagModal(false);
            setBulkTags([]);
            setSelectedIds(new Set());
            loadContacts();
        } catch (err) {
            // BUG-041 — old empty catch hid 403/permission errors as "silently
            // fails" (Joseph's report). Surface the server detail so users can
            // see whether it's a permission issue, validation, or something else.
            const detail = err?.response?.data?.detail || err?.message || 'Network error';
            toast.error(`Failed to update tags: ${detail}`, { duration: 8000 });
            console.error('Bulk-tag failed:', err);
        }
        finally { setBulkTagging(false); }
    };

    const isAdmin = user?.is_admin || user?.is_super_admin;

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Contacts</h1>
                    <div className="flex items-center gap-2 mt-1">
                        <Users className="w-3.5 h-3.5 text-stone-400" />
                        <span className="text-sm text-stone-500" data-field="contact.count">{activeTab === 'deleted' ? `${deletedTotal} deleted` : `${total} contacts`}</span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {/* Bulk action buttons */}
                    {selectedIds.size > 0 && activeTab === 'contacts' && (
                        <>
                            <button onClick={() => { setBulkTags([]); setBulkTagAction('add'); setShowBulkTagModal(true); }}
                                className="flex items-center gap-2 px-4 py-2 bg-stone-700 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors">
                                <Tag className="w-4 h-4" /> Tag ({selectedIds.size})
                            </button>
                            <button onClick={() => { setSelectedCampaignId(''); loadCampaignsForEnroll(); setShowBulkEnrollModal(true); }}
                                className="flex items-center gap-2 px-4 py-2 bg-stone-700 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors">
                                <Send className="w-4 h-4" /> Enroll ({selectedIds.size})
                            </button>
                            <button onClick={() => { setDeleteConfirmText(''); setShowDeleteModal(true); }}
                                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors">
                                <Trash2 className="w-4 h-4" /> Delete ({selectedIds.size})
                            </button>
                        </>
                    )}
                    {selectedIds.size > 0 && activeTab === 'deleted' && (
                        <button onClick={() => handleRestore()} disabled={restoring}
                            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50">
                            <RotateCcw className="w-4 h-4" /> Restore ({selectedIds.size})
                        </button>
                    )}
                    {/* Tab buttons */}
                    {isAdmin && (
                        <button onClick={() => { setActiveTab(activeTab === 'deleted' ? 'contacts' : 'deleted'); setSelectedIds(new Set()); }}
                            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                activeTab === 'deleted' ? 'bg-red-50 text-red-700 border border-red-200' : 'text-stone-500 hover:text-stone-700 hover:bg-stone-100'
                            }`}>
                            <Trash2 className="w-3.5 h-3.5" /> Deleted
                        </button>
                    )}
                    <button onClick={() => { setEditContact(null); setShowModal(true); }} data-testid="new-contact-btn"
                        className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors">
                        <Plus className="w-4 h-4" /> New Contact
                    </button>
                </div>
            </div>

            {/* Toolbar: View Selector + Filters + Add Column */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 flex-1 flex-wrap">
                    <ViewSelector
                        views={views}
                        activeViewId={activeViewId}
                        isDirty={isDirty}
                        onSwitchView={switchView}
                        onSaveView={saveView}
                        onSaveAsView={saveAsView}
                        onDeleteView={deleteView}
                        onPushToOrg={pushToOrg}
                        isAdmin={isAdmin}
                    />
                    <div className="w-px h-6 bg-stone-200" />
                    <div className="relative flex-1 max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <input type="text" placeholder="Search contacts..." value={search} onChange={e => setSearch(e.target.value)}
                            data-testid="contacts-search-input"
                            className="w-full pl-9 pr-3 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    </div>
                    <select value={filterOwner} onChange={e => setFilterOwner(e.target.value)}
                        className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                        <option value="">All Owners</option>
                        {teamMembers.map(m => (
                            <option key={m.user_id} value={m.user_id}>{m.name}</option>
                        ))}
                    </select>
                    <select value={filterSeniority} onChange={e => setFilterSeniority(e.target.value)}
                        className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                        <option value="">All Seniority</option>
                        {SENIORITY.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {/* v2.16.39 BUG-039: tag filter */}
                    <select value={filterTag} onChange={e => setFilterTag(e.target.value)}
                        className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none"
                        title="Filter contacts by tag">
                        <option value="">All Tags</option>
                        {tagOptions.map(t => (
                            <option key={t.tag} value={t.tag}>
                                {t.tag}{typeof t.count === 'number' ? ` (${t.count})` : ''}
                            </option>
                        ))}
                    </select>
                </div>
                <AddColumnPanel
                    allFields={allFields}
                    visibleKeys={visibleKeySet}
                    onAddColumn={addColumn}
                />
            </div>

            {/* Data Table */}
            {activeTab === 'contacts' ? (
                loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                    </div>
                ) : (
                    <DataTable
                        data={contacts}
                        visibleColumns={visibleColumns}
                        sorting={sorting}
                        onSortingChange={setSorting}
                        columnSizing={columnSizing}
                        onColumnSizingChange={setColumnSizing}
                        onRowClick={(row) => navigate(`/contacts/${row.contact_id}`)}
                        selectable
                        selectedIds={selectedIds}
                        onSelectionChange={setSelectedIds}
                        idField="contact_id"
                    />
                )
            ) : (
                /* Deleted contacts table */
                <div className="overflow-auto bg-white rounded-xl border border-stone-200">
                    <table className="w-full">
                        <thead className="sticky top-0 z-10 bg-stone-50 border-b border-stone-200">
                            <tr>
                                <th className="px-3 py-2.5 text-left w-10">
                                    <input type="checkbox"
                                        checked={deletedContacts.length > 0 && deletedContacts.every(c => selectedIds.has(c.contact_id))}
                                        ref={el => { if (el) el.indeterminate = deletedContacts.some(c => selectedIds.has(c.contact_id)) && !deletedContacts.every(c => selectedIds.has(c.contact_id)); }}
                                        onChange={(e) => {
                                            if (e.target.checked) setSelectedIds(new Set(deletedContacts.map(c => c.contact_id)));
                                            else setSelectedIds(new Set());
                                        }}
                                        className="w-4 h-4 rounded border-stone-300 cursor-pointer" />
                                </th>
                                {['Name', 'Email', 'Title', 'Deleted By', 'Deleted At', ''].map(h => (
                                    <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {deletedContacts.length === 0 ? (
                                <tr><td colSpan={7} className="text-center py-16 text-sm text-stone-400">No deleted contacts</td></tr>
                            ) : deletedContacts.map(c => (
                                <tr key={c.contact_id} className="border-b border-stone-100 hover:bg-stone-50">
                                    <td className="px-3 py-2.5">
                                        <input type="checkbox" checked={selectedIds.has(c.contact_id)}
                                            onChange={(e) => {
                                                const next = new Set(selectedIds);
                                                if (e.target.checked) next.add(c.contact_id);
                                                else next.delete(c.contact_id);
                                                setSelectedIds(next);
                                            }}
                                            className="w-4 h-4 rounded border-stone-300 cursor-pointer" />
                                    </td>
                                    <td className="px-3 py-2.5 text-sm font-medium text-stone-900">{c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim()}</td>
                                    <td className="px-3 py-2.5 text-sm text-stone-600">{c.email || '—'}</td>
                                    <td className="px-3 py-2.5 text-sm text-stone-600">{c.title || '—'}</td>
                                    <td className="px-3 py-2.5 text-sm text-stone-500">{c.deleted_by_name || '—'}</td>
                                    <td className="px-3 py-2.5 text-sm text-stone-500">{c.deleted_at ? new Date(c.deleted_at).toLocaleDateString() : '—'}</td>
                                    <td className="px-3 py-2.5">
                                        <button onClick={() => handleRestore([c.contact_id])}
                                            className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
                                            <RotateCcw className="w-3 h-3" /> Restore
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Delete {selectedIds.size} Contact{selectedIds.size > 1 ? 's' : ''}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <p className="text-sm text-stone-600">
                            This will soft-delete the selected contact{selectedIds.size > 1 ? 's' : ''}. They can be restored from the "Deleted" tab.
                        </p>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">
                                Type DELETE to confirm
                            </label>
                            <input type="text" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)}
                                placeholder="DELETE" autoFocus
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400" />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                            <button onClick={handleBulkDelete} disabled={deleteConfirmText !== 'DELETE' || deleting}
                                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Bulk Tag Modal */}
            <Dialog open={showBulkTagModal} onOpenChange={setShowBulkTagModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-stone-900">
                            <Tag className="w-5 h-5" />
                            Tag {selectedIds.size} Contact{selectedIds.size > 1 ? 's' : ''}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Action</label>
                            <select value={bulkTagAction} onChange={e => setBulkTagAction(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                <option value="add">Add Tags</option>
                                <option value="remove">Remove Tags</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Tags</label>
                            <TagInput
                                value={bulkTags}
                                onChange={setBulkTags}
                                placeholder={bulkTagAction === 'add' ? 'Add tags...' : 'Tags to remove...'}
                            />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setShowBulkTagModal(false)}
                                className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                            <button onClick={handleBulkTag} disabled={!bulkTags.length || bulkTagging}
                                className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed">
                                {bulkTagging ? 'Updating...' : `${bulkTagAction === 'add' ? 'Add' : 'Remove'} Tags`}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* BUG-040 — Bulk Enroll Modal */}
            <Dialog open={showBulkEnrollModal} onOpenChange={setShowBulkEnrollModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-stone-900">
                            <Send className="w-5 h-5" />
                            Enroll {selectedIds.size} Contact{selectedIds.size > 1 ? 's' : ''} in a Campaign
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Campaign</label>
                            <select value={selectedCampaignId} onChange={e => setSelectedCampaignId(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                <option value="">— Select a campaign —</option>
                                {campaignsList.map(c => (
                                    <option key={c.campaign_id} value={c.campaign_id}>
                                        {c.name} {c.status && c.status !== 'active' ? `[${c.status}]` : ''} · {(c.steps || []).length} steps
                                    </option>
                                ))}
                            </select>
                            {campaignsList.length === 0 && (
                                <p className="text-xs text-stone-400 mt-1">No campaigns found.</p>
                            )}
                        </div>
                        <p className="text-xs text-stone-500">
                            Contacts already enrolled (active or paused) or missing required data will be skipped.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setShowBulkEnrollModal(false)}
                                className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                            <button onClick={handleBulkEnroll} disabled={!selectedCampaignId || enrolling}
                                className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed">
                                {enrolling ? 'Enrolling...' : 'Enroll'}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Contact Modal */}
            <ContactModal
                open={showModal}
                onClose={() => { setShowModal(false); setEditContact(null); }}
                contact={editContact}
                accounts={accounts}
                onSave={handleSave}
                onAccountCreated={(acc) => setAccounts(prev => [acc, ...prev])}
            />
        </div>
    );
}
